import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { Container, Button, IconButton, Box, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { Account } from '@toolpad/core/Account';
import { useColorScheme } from '@mui/material/styles';
import { setupEmailTemplatesListener, cleanupEmailTemplatesListener } from 'src/store/slices/staffGlobalSlice';
import { useSession } from 'src/SessionContext';
import { useDispatch, useSelector } from 'src/store';
import { RootState } from 'shared/types/appTypes';
import StaffTitle from 'src/layouts/components/StaffTitle';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import TalentView from 'src/pages/talent-view/components/TalentView';
import { getNavigation } from './AppConfig';

function CustomAccount() {
  return (
    <Account
      slotProps={{
        preview: { slotProps: { avatarIconButton: { sx: { border: '0' } } } }
      }}
    />
  );
}

function ToolbarActions() {
  const { session } = useSession() || {};
  const { dayliteId } = session || {};
  const [talentViewOpen, setTalentViewOpen] = useState(false);
  const { mode, setMode } = useColorScheme();

  const handleTalentViewOpen = () => {
    setTalentViewOpen(true);
  };

  const handleTalentViewClose = () => {
    setTalentViewOpen(false);
  };

  const handleColorModeToggle = () => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Color mode toggle button */}
        <IconButton
          onClick={handleColorModeToggle}
          size="medium"
          aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>
        
        {/* Talent View button */}
        <Button 
          variant="outlined" 
          size="small" 
          startIcon={<PersonIcon />}
          onClick={handleTalentViewOpen}
          disabled={!dayliteId}
        >
          Talent View
        </Button>
      </Box>

      {/* Full-screen modal for TalentView */}
      <Dialog
        fullScreen
        open={talentViewOpen}
        onClose={handleTalentViewClose}
        aria-labelledby="talent-view-dialog-title"
        PaperProps={{
          sx: (theme) => theme.applyStyles('dark', {
            backgroundColor: theme.palette.background.default
          })
        }}
      >
        <DialogTitle id="talent-view-dialog-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Talent View
          <IconButton
            edge="end"
            color="primary"
            onClick={handleTalentViewClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {dayliteId && <TalentView talentId={dayliteId} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function StaffLayout() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { session } = useSession() || {};
  const { accountType } = session || {};
  
  // Get active event ID from Redux to generate dynamic navigation
  const activeEventId = useSelector((state: RootState) => state.activeEvent.id);
  
  // Only show event navigation when on an event route
  const isOnEventRoute = location.pathname.startsWith('/event/');
  const navigation = getNavigation(isOnEventRoute && activeEventId ? activeEventId : undefined);

  // Set up staff-specific resources
  useEffect(() => {
    if (accountType === 'Staff') {
      dispatch(setupEmailTemplatesListener());
    }

    return () => {
      dispatch(cleanupEmailTemplatesListener());
    };
  }, [dispatch, accountType]);

  return (
    <DashboardLayout 
      defaultSidebarCollapsed={true}
      navigation={navigation}
      slots={{ 
        toolbarAccount: CustomAccount, 
        appTitle: StaffTitle,
        toolbarActions: ToolbarActions
      }}
      sx={{
        '& .MuiListItem-root': {
          transition: 'opacity 0.3s ease-in-out, max-height 0.3s ease-in-out, margin 0.3s ease-in-out, padding 0.3s ease-in-out',
          overflow: 'hidden'
        },
        '& .MuiListItemButton-root': {
          transition: 'opacity 0.3s ease-in-out'
        }
      }}
    >
      <Container maxWidth={false} sx={{ pt: 3 }}>
        <Outlet />
      </Container>
    </DashboardLayout>
  );
}
